import { useCallback, useEffect, useRef, useState } from 'react';

const HANDLE = 9;
const CORNER_COLORS = ['#4ECDC4', '#845EF7', '#FF6B6B', '#FFD43B'];

/** Default inset quad in normalized 0–1 coords (TL, TR, BR, BL). */
export function defaultNormalizedCorners() {
    return [
        { x: 0.08, y: 0.08 },
        { x: 0.92, y: 0.08 },
        { x: 0.92, y: 0.92 },
        { x: 0.08, y: 0.92 },
    ];
}

/** Axis-aligned bounds from a normalized quad — used for thumb badges. */
export function cornersToRegion(corners) {
    if (!Array.isArray(corners) || corners.length < 4) return null;
    const xs = corners.map((c) => c.x);
    const ys = corners.map((c) => c.y);
    const left = Math.min(...xs);
    const top = Math.min(...ys);
    const right = Math.max(...xs);
    const bottom = Math.max(...ys);
    const width = right - left;
    const height = bottom - top;
    if (width <= 0.01 || height <= 0.01) return null;
    return { left, top, width, height };
}

/**
 * 4-corner perspective crop overlay (same interaction model as image Flatten).
 * Corners are normalized 0–1 relative to the image: TL, TR, BR, BL.
 */
export default function PerspectiveQuadCrop({
    src,
    value,
    onChange,
    className = 'max-h-[72vh] max-w-full',
    /** Inline size cap — a full-screen host fits the image to its stage instead. */
    imgStyle,
}) {
    const imgRef = useRef(null);
    const [nw, setNw] = useState(0);
    const [nh, setNh] = useState(0);
    const [box, setBox] = useState({ w: 0, h: 0 });
    const cornersRef = useRef(value?.length === 4 ? value.map((c) => ({ ...c })) : defaultNormalizedCorners());
    const [, bump] = useState(0);
    const force = () => bump((n) => n + 1);
    const dragRef = useRef(null);
    const layoutRef = useRef({ scale: 1 });
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;

    const emit = useCallback(() => {
        onChangeRef.current?.(cornersRef.current.map((c) => ({ x: c.x, y: c.y })));
    }, []);

    const measure = useCallback(() => {
        const img = imgRef.current;
        if (!img?.naturalWidth) return;
        const rect = img.getBoundingClientRect();
        layoutRef.current = { scale: rect.width / img.naturalWidth };
        setBox({ w: rect.width, h: rect.height });
    }, []);

    const normToDisp = useCallback((c) => {
        const { scale } = layoutRef.current;
        return [c.x * nw * scale, c.y * nh * scale];
    }, [nw, nh]);

    const dispToNorm = useCallback(([dx, dy]) => {
        const { scale } = layoutRef.current;
        const natX = dx / scale;
        const natY = dy / scale;
        return {
            x: Math.max(0, Math.min(1, natX / (nw || 1))),
            y: Math.max(0, Math.min(1, natY / (nh || 1))),
        };
    }, [nw, nh]);

    // Prefer initial value from parent when the image loads / remounts.
    // Drag updates go through onChange; avoid fighting mid-drag re-syncs.
    const onImgLoad = useCallback(() => {
        const img = imgRef.current;
        if (!img) return;
        setNw(img.naturalWidth);
        setNh(img.naturalHeight);
        if (value?.length === 4) {
            cornersRef.current = value.map((c) => ({ x: c.x, y: c.y }));
        } else {
            cornersRef.current = defaultNormalizedCorners();
        }
        requestAnimationFrame(() => {
            measure();
            force();
            emit();
        });
    }, [emit, measure, value]);

    useEffect(() => {
        const img = imgRef.current;
        if (!img) return undefined;
        const ro = new ResizeObserver(() => {
            measure();
            force();
        });
        ro.observe(img);
        return () => ro.disconnect();
    }, [src, measure]);

    const dragMatchesPointer = useCallback((e, d) => {
        if (!d) return false;
        if (d.pointerType === 'mouse' && e.pointerType === 'mouse') return true;
        return d.pointerId === e.pointerId;
    }, []);

    useEffect(() => {
        const onMove = (e) => {
            const d = dragRef.current;
            if (!d || !dragMatchesPointer(e, d)) return;
            const img = imgRef.current;
            if (!img) return;
            const r = img.getBoundingClientRect();
            const lx = e.clientX - r.left;
            const ly = e.clientY - r.top;
            if (d.kind === 'corner') {
                cornersRef.current[d.cornerIndex] = dispToNorm([lx, ly]);
            } else if (d.kind === 'pan') {
                const cur = dispToNorm([lx, ly]);
                const dx = cur.x - d.startNorm.x;
                const dy = cur.y - d.startNorm.y;
                cornersRef.current = d.startCorners.map((c) => ({
                    x: Math.max(0, Math.min(1, c.x + dx)),
                    y: Math.max(0, Math.min(1, c.y + dy)),
                }));
            }
            force();
            emit();
        };
        const onUp = (e) => {
            const d = dragRef.current;
            if (!d || !dragMatchesPointer(e, d)) return;
            dragRef.current = null;
        };
        document.addEventListener('pointermove', onMove);
        document.addEventListener('pointerup', onUp);
        document.addEventListener('pointercancel', onUp);
        return () => {
            document.removeEventListener('pointermove', onMove);
            document.removeEventListener('pointerup', onUp);
            document.removeEventListener('pointercancel', onUp);
        };
    }, [dispToNorm, dragMatchesPointer, emit]);

    const onPointerDownHandle = useCallback((cornerIndex) => (e) => {
        e.preventDefault();
        e.stopPropagation();
        dragRef.current = {
            kind: 'corner',
            cornerIndex,
            pointerId: e.pointerId,
            pointerType: e.pointerType,
        };
        try {
            e.currentTarget.setPointerCapture(e.pointerId);
        } catch {
            /* ignore */
        }
    }, []);

    const onPointerDownPan = useCallback((e) => {
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        e.preventDefault();
        const img = imgRef.current;
        if (!img || !nw || !nh) return;
        const r = img.getBoundingClientRect();
        const lx = e.clientX - r.left;
        const ly = e.clientY - r.top;
        dragRef.current = {
            kind: 'pan',
            pointerId: e.pointerId,
            pointerType: e.pointerType,
            startNorm: dispToNorm([lx, ly]),
            startCorners: cornersRef.current.map((c) => ({ ...c })),
        };
        try {
            e.currentTarget.setPointerCapture(e.pointerId);
        } catch {
            /* ignore */
        }
    }, [dispToNorm, nw, nh]);

    const corners = cornersRef.current;
    const disp = corners.map(normToDisp);
    const polyPts = disp.map(([x, y]) => `${x},${y}`).join(' ');
    const holePath = disp.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x},${y}`).join(' ') + ' Z';
    const dimMask = box.w > 0
        ? `M0,0 H${box.w} V${box.h} H0 Z ${holePath}`
        : '';

    return (
        <div className="relative inline-block max-w-full select-none touch-none">
            <img
                ref={imgRef}
                src={src}
                alt=""
                draggable={false}
                onLoad={onImgLoad}
                style={imgStyle}
                className={`block h-auto w-auto object-contain ${className}`}
            />
            {nw > 0 && nh > 0 && box.w > 0 ? (
                <svg
                    width={box.w}
                    height={box.h}
                    className="pointer-events-none absolute left-0 top-0 overflow-visible"
                >
                    <path d={dimMask} fill="rgba(0,0,0,0.45)" fillRule="evenodd" />
                    <polygon
                        points={polyPts}
                        fill="rgba(255,255,255,0.06)"
                        stroke="rgba(255,255,255,0.9)"
                        strokeWidth={2}
                        strokeDasharray="8 4"
                        style={{ pointerEvents: 'visiblePainted', cursor: 'move' }}
                        onPointerDown={onPointerDownPan}
                    />
                    {disp.map(([x, y], i) => (
                        <g key={i}>
                            <circle
                                cx={x}
                                cy={y}
                                r={HANDLE + 2}
                                fill="#fff"
                                stroke={CORNER_COLORS[i]}
                                strokeWidth={3}
                                style={{ pointerEvents: 'all', cursor: 'grab' }}
                                onPointerDown={onPointerDownHandle(i)}
                            />
                            <circle
                                cx={x}
                                cy={y}
                                r={3}
                                fill={CORNER_COLORS[i]}
                                style={{ pointerEvents: 'none' }}
                            />
                        </g>
                    ))}
                </svg>
            ) : null}
        </div>
    );
}
